import React, { useMemo, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  UserCheck, 
  UserX, 
  AlertTriangle,
  Gift,
  TrendingUp,
  BarChart3,
  CreditCard,
  DollarSign,
  RefreshCw,
  Zap,
  MapPin,
  Loader2
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentMonthYear } from './utils';
import { getExpirationStatus } from './ExpirationAlert';
import type { MemberData, Payment } from './types';
import { format, parseISO, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';

interface AdminStatsDashboardProps {
  members: MemberData[];
}

const PLAN_COLORS = {
  bronce: '#D97706', // amber-600
  plata: '#64748B', // slate-500
  oro: '#EAB308', // yellow-500
};

const STATUS_COLORS = {
  active: '#22C55E', // green-500
  inactive: '#EF4444', // red-500
};

const PERIOD_COLORS = {
  mensual: '#3B82F6', // blue-500
  trimestral: '#8B5CF6', // violet-500
  anual: '#F59E0B', // amber-500
};

const PAYMENT_COLORS = {
  activation: '#22C55E', // green-500
  renewal: '#3B82F6', // blue-500
};

// Format large amounts compactly (e.g., ₡741K instead of ₡741,000)
const formatCompactAmount = (amount: number): string => {
  if (amount >= 1_000_000) {
    return `₡${(amount / 1_000_000).toFixed(1).replace('.0', '')}M`;
  } else if (amount >= 1_000) {
    return `₡${(amount / 1_000).toFixed(0)}K`;
  }
  return `₡${amount.toLocaleString('es-CR')}`;
};

interface StoreStats {
  tienda: string;
  ventas: number;
  monto: number;
  canjes: number;
  ahorros: number;
}

export const AdminStatsDashboard: React.FC<AdminStatsDashboardProps> = ({ members }) => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [storeStats, setStoreStats] = useState<StoreStats[]>([]);
  const [loadingStores, setLoadingStores] = useState(true);

  useEffect(() => {
    const fetchPayments = async () => {
      const sixMonthsAgo = subMonths(new Date(), 6);
      const { data } = await supabase
        .from('membership_payments')
        .select('*')
        .gte('created_at', sixMonthsAgo.toISOString())
        .order('created_at', { ascending: false });
      
      setPayments((data as Payment[]) || []);
      setLoadingPayments(false);
    };
    fetchPayments();
  }, []);

  useEffect(() => {
    const fetchStoreStats = async () => {
      setLoadingStores(true);
      try {
        // Get all sellers with their store locations
        const { data: sellers } = await supabase
          .from('profiles')
          .select('user_id, ubicacion_tienda')
          .not('ubicacion_tienda', 'is', null)
          .neq('ubicacion_tienda', '');

        // Get sales data
        const { data: sales } = await supabase
          .from('discount_purchases')
          .select('registered_by, final_price, savings');

        // Get redemptions data
        const { data: redemptions } = await supabase
          .from('benefit_redemptions')
          .select('redeemed_by, quantity');

        // Initialize store map
        const storeMap = new Map<string, StoreStats>();
        
        sellers?.forEach(seller => {
          if (seller.ubicacion_tienda && !storeMap.has(seller.ubicacion_tienda)) {
            storeMap.set(seller.ubicacion_tienda, {
              tienda: seller.ubicacion_tienda,
              ventas: 0,
              monto: 0,
              canjes: 0,
              ahorros: 0,
            });
          }
        });

        // Aggregate sales by store
        sales?.forEach(sale => {
          const seller = sellers?.find(s => s.user_id === sale.registered_by);
          if (seller?.ubicacion_tienda) {
            const store = storeMap.get(seller.ubicacion_tienda);
            if (store) {
              store.ventas += 1;
              store.monto += sale.final_price || 0;
              store.ahorros += sale.savings || 0;
            }
          }
        });

        // Aggregate redemptions by store
        redemptions?.forEach(redemption => {
          const seller = sellers?.find(s => s.user_id === redemption.redeemed_by);
          if (seller?.ubicacion_tienda) {
            const store = storeMap.get(seller.ubicacion_tienda);
            if (store) {
              store.canjes += redemption.quantity || 0;
            }
          }
        });

        // Convert to sorted array by monto
        const sortedStats = Array.from(storeMap.values())
          .sort((a, b) => b.monto - a.monto);
        
        setStoreStats(sortedStats);
      } catch (error) {
        console.error('Error fetching store stats:', error);
      } finally {
        setLoadingStores(false);
      }
    };

    fetchStoreStats();
  }, []);

  const stats = useMemo(() => {
    const currentMonth = getCurrentMonthYear();
    
    const activeMemberships = members.filter(m => m.membership?.is_active);
    const inactiveMemberships = members.filter(m => m.membership && !m.membership.is_active);
    const expiringMemberships = members.filter(m => {
      if (!m.membership) return false;
      const status = getExpirationStatus(m.membership);
      return status.isExpiring;
    });
    
    const monthlyRedemptions = members.reduce((acc, m) => {
      return acc + m.redemptions.filter(r => r.month_year === currentMonth).length;
    }, 0);

    // Distribution by plan
    const planDistribution = Object.entries(
      members.reduce((acc, m) => {
        if (m.membership) {
          acc[m.membership.plan] = (acc[m.membership.plan] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>)
    ).map(([name, value]) => ({ name, value }));

    // Distribution by billing period
    const periodDistribution = Object.entries(
      members.reduce((acc, m) => {
        if (m.membership) {
          acc[m.membership.billing_period] = (acc[m.membership.billing_period] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>)
    ).map(([name, value]) => ({ name, value }));

    // Status distribution
    const statusDistribution = [
      { name: 'Activos', value: activeMemberships.length },
      { name: 'Cancelados', value: inactiveMemberships.length },
    ];

    return {
      total: members.length,
      active: activeMemberships.length,
      inactive: inactiveMemberships.length,
      expiring: expiringMemberships.length,
      redemptionsThisMonth: monthlyRedemptions,
      planDistribution,
      periodDistribution,
      statusDistribution,
    };
  }, [members]);

  const paymentStats = useMemo(() => {
    // Group payments by month
    const monthlyData: Record<string, { activations: number; renewals: number; total: number }> = {};
    
    payments.forEach(payment => {
      const monthKey = format(parseISO(payment.created_at), 'yyyy-MM');
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { activations: 0, renewals: 0, total: 0 };
      }
      
      if (payment.payment_type === 'activation') {
        monthlyData[monthKey].activations += payment.amount;
      } else {
        monthlyData[monthKey].renewals += payment.amount;
      }
      monthlyData[monthKey].total += payment.amount;
    });

    // Convert to array and sort by month
    const monthlyPayments = Object.entries(monthlyData)
      .map(([month, data]) => ({
        month,
        monthLabel: format(parseISO(month + '-01'), 'MMM yyyy', { locale: es }),
        ...data,
      }))
      .sort((a, b) => a.month.localeCompare(b.month))
      .slice(-6); // Last 6 months

    // Current month totals
    const currentMonthKey = format(new Date(), 'yyyy-MM');
    const currentMonthData = monthlyData[currentMonthKey] || { activations: 0, renewals: 0, total: 0 };

    // Total overall
    const totalRevenue = payments.reduce((sum, p) => sum + p.amount, 0);
    const totalActivations = payments.filter(p => p.payment_type === 'activation').reduce((sum, p) => sum + p.amount, 0);
    const totalRenewals = payments.filter(p => p.payment_type === 'renewal').reduce((sum, p) => sum + p.amount, 0);

    return {
      monthlyPayments,
      currentMonth: currentMonthData,
      totalRevenue,
      totalActivations,
      totalRenewals,
      activationCount: payments.filter(p => p.payment_type === 'activation').length,
      renewalCount: payments.filter(p => p.payment_type === 'renewal').length,
    };
  }, [payments]);

  const StatCard = ({ 
    icon: Icon, 
    label, 
    value, 
    color 
  }: { 
    icon: React.ElementType; 
    label: string; 
    value: number | string; 
    color: string;
  }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/5 backdrop-blur-xl rounded-xl p-4 border border-white/10"
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xl sm:text-2xl font-bold text-white truncate">{value}</p>
          <p className="text-white/60 text-sm">{label}</p>
        </div>
      </div>
    </motion.div>
  );

  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ name: string; value: number }> }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-800 border border-white/20 rounded-lg p-2 text-sm">
          <p className="text-white capitalize">{payload[0].name}: {payload[0].value}</p>
        </div>
      );
    }
    return null;
  };

  const PaymentTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ name: string; value: number; fill: string }>; label?: string }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-800 border border-white/20 rounded-lg p-3 text-sm">
          <p className="text-white font-medium mb-2 capitalize">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-white/80">
              <span style={{ color: entry.fill }}>{entry.name === 'activations' ? 'Activaciones' : 'Renovaciones'}:</span>{' '}
              ₡{entry.value.toLocaleString('es-CR')}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6 mt-6"
    >
      {/* Main Stats Dashboard */}
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
        <div className="flex items-center gap-2 mb-6">
          <BarChart3 className="w-5 h-5 text-amber-500" />
          <h2 className="text-lg font-bold text-white">Panel de Estadísticas</h2>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={UserCheck}
            label="Activos"
            value={stats.active}
            color="bg-green-500/20"
          />
          <StatCard
            icon={UserX}
            label="Cancelados"
            value={stats.inactive}
            color="bg-red-500/20"
          />
          <StatCard
            icon={AlertTriangle}
            label="Por vencer"
            value={stats.expiring}
            color="bg-amber-500/20"
          />
          <StatCard
            icon={Gift}
            label="Canjes del mes"
            value={stats.redemptionsThisMonth}
            color="bg-blue-500/20"
          />
        </div>

        {/* Charts grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Plan distribution pie chart */}
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <h3 className="text-white/70 text-sm font-medium mb-4 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Distribución por Plan
            </h3>
            {stats.planDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={stats.planDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={4}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {stats.planDistribution.map((entry) => (
                      <Cell 
                        key={entry.name} 
                        fill={PLAN_COLORS[entry.name as keyof typeof PLAN_COLORS] || '#666'}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[180px] flex items-center justify-center text-white/40 text-sm">
                Sin datos
              </div>
            )}
          </div>

          {/* Period distribution bar chart */}
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <h3 className="text-white/70 text-sm font-medium mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Por Período de Facturación
            </h3>
            {stats.periodDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={stats.periodDistribution} layout="vertical">
                  <XAxis type="number" hide />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    tick={{ fill: '#fff', fontSize: 12 }}
                    width={80}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="value" 
                    radius={[0, 4, 4, 0]}
                  >
                    {stats.periodDistribution.map((entry) => (
                      <Cell 
                        key={entry.name} 
                        fill={PERIOD_COLORS[entry.name as keyof typeof PERIOD_COLORS] || '#666'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[180px] flex items-center justify-center text-white/40 text-sm">
                Sin datos
              </div>
            )}
          </div>

          {/* Status distribution pie chart */}
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <h3 className="text-white/70 text-sm font-medium mb-4 flex items-center gap-2">
              <UserCheck className="w-4 h-4" />
              Estado de Membresías
            </h3>
            {stats.statusDistribution.some(d => d.value > 0) ? (
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={stats.statusDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={4}
                    dataKey="value"
                    label={({ name, percent }) => percent > 0 ? `${name} ${(percent * 100).toFixed(0)}%` : ''}
                    labelLine={false}
                  >
                    <Cell fill={STATUS_COLORS.active} />
                    <Cell fill={STATUS_COLORS.inactive} />
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[180px] flex items-center justify-center text-white/40 text-sm">
                Sin datos
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Payment Stats Dashboard */}
      <div className="bg-gradient-to-br from-green-500/10 to-blue-500/10 backdrop-blur-xl rounded-2xl p-6 border border-green-500/20">
        <div className="flex items-center gap-2 mb-6">
          <CreditCard className="w-5 h-5 text-green-400" />
          <h2 className="text-lg font-bold text-white">Resumen de Pagos</h2>
        </div>

        {/* Payment stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={DollarSign}
            label="Este mes"
            value={formatCompactAmount(paymentStats.currentMonth.total)}
            color="bg-green-500/20"
          />
          <StatCard
            icon={Zap}
            label="Activaciones"
            value={paymentStats.activationCount}
            color="bg-emerald-500/20"
          />
          <StatCard
            icon={RefreshCw}
            label="Renovaciones"
            value={paymentStats.renewalCount}
            color="bg-blue-500/20"
          />
          <StatCard
            icon={TrendingUp}
            label="Total 6 meses"
            value={formatCompactAmount(paymentStats.totalRevenue)}
            color="bg-amber-500/20"
          />
        </div>

        {/* Monthly payments chart */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Bar chart - Monthly revenue */}
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <h3 className="text-white/70 text-sm font-medium mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Ingresos por Mes (últimos 6 meses)
            </h3>
            {paymentStats.monthlyPayments.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={paymentStats.monthlyPayments}>
                  <XAxis 
                    dataKey="monthLabel" 
                    tick={{ fill: '#fff', fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis 
                    tick={{ fill: '#fff', fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value) => `₡${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip content={<PaymentTooltip />} />
                  <Legend 
                    formatter={(value) => value === 'activations' ? 'Activaciones' : 'Renovaciones'}
                    wrapperStyle={{ fontSize: '12px' }}
                  />
                  <Bar 
                    dataKey="activations" 
                    stackId="a" 
                    fill={PAYMENT_COLORS.activation}
                    radius={[0, 0, 0, 0]}
                    name="activations"
                  />
                  <Bar 
                    dataKey="renewals" 
                    stackId="a" 
                    fill={PAYMENT_COLORS.renewal}
                    radius={[4, 4, 0, 0]}
                    name="renewals"
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-white/40 text-sm">
                {loadingPayments ? 'Cargando...' : 'Sin pagos registrados'}
              </div>
            )}
          </div>

          {/* Summary cards */}
          <div className="space-y-4">
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <h3 className="text-white/70 text-sm font-medium mb-3">Desglose Total</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-white/80 text-sm">Activaciones</span>
                  </div>
                  <span className="text-green-400 font-semibold">
                    ₡{paymentStats.totalActivations.toLocaleString('es-CR')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-white/80 text-sm">Renovaciones</span>
                  </div>
                  <span className="text-blue-400 font-semibold">
                    ₡{paymentStats.totalRenewals.toLocaleString('es-CR')}
                  </span>
                </div>
                <div className="border-t border-white/10 pt-3 flex items-center justify-between">
                  <span className="text-white font-medium">Total</span>
                  <span className="text-white font-bold text-lg">
                    ₡{paymentStats.totalRevenue.toLocaleString('es-CR')}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl p-4 border border-green-500/30">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-green-500/30">
                  <DollarSign className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-white/60 text-xs">Promedio mensual</p>
                  <p className="text-white font-bold text-xl">
                    ₡{paymentStats.monthlyPayments.length > 0 
                      ? Math.round(paymentStats.totalRevenue / paymentStats.monthlyPayments.length).toLocaleString('es-CR')
                      : '0'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Store Performance Dashboard */}
      <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 backdrop-blur-xl rounded-2xl p-6 border border-purple-500/20">
        <div className="flex items-center gap-2 mb-6">
          <MapPin className="w-5 h-5 text-purple-400" />
          <h2 className="text-lg font-bold text-white">Rendimiento por Tienda</h2>
        </div>

        {loadingStores ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
          </div>
        ) : storeStats.length > 0 ? (
          <div className="grid gap-6">
            {/* Bar Chart - Sales by Store */}
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <h3 className="text-white/70 text-sm font-medium mb-4 flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Ventas por Tienda
              </h3>
              <ResponsiveContainer width="100%" height={Math.max(180, storeStats.length * 45)}>
                <BarChart data={storeStats} layout="vertical">
                  <XAxis 
                    type="number" 
                    tick={{ fill: '#fff', fontSize: 10 }}
                    tickFormatter={(value) => `₡${(value / 1000).toFixed(0)}k`}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis 
                    dataKey="tienda" 
                    type="category" 
                    width={140}
                    tick={{ fill: '#fff', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip 
                    formatter={(value: number) => [`₡${value.toLocaleString('es-CR')}`, 'Monto']}
                    contentStyle={{ 
                      backgroundColor: '#1e293b', 
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar 
                    dataKey="monto" 
                    fill="#A855F7" 
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Store Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {storeStats.map((store, index) => (
                <div 
                  key={store.tienda}
                  className="bg-white/5 rounded-xl p-4 border border-white/10 hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      index === 0 ? 'bg-purple-500/30' : 'bg-purple-500/20'
                    }`}>
                      <span className={`text-xs font-bold ${
                        index === 0 ? 'text-purple-300' : 'text-purple-400'
                      }`}>#{index + 1}</span>
                    </div>
                    <h4 className="text-white font-medium text-sm truncate flex-1">{store.tienda}</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-white/5 rounded-lg p-2">
                      <p className="text-white/60">Ventas</p>
                      <p className="text-white font-bold">{store.ventas}</p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2">
                      <p className="text-white/60">Monto</p>
                      <p className="text-green-400 font-bold">{formatCompactAmount(store.monto)}</p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2">
                      <p className="text-white/60">Canjes</p>
                      <p className="text-blue-400 font-bold">{store.canjes}</p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2">
                      <p className="text-white/60">Ahorros</p>
                      <p className="text-amber-400 font-bold">{formatCompactAmount(store.ahorros)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="h-48 flex items-center justify-center text-white/40">
            No hay datos de tiendas disponibles
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default AdminStatsDashboard;
